import { SagaIterator } from 'redux-saga';
import { all, call, CallEffect, cancel, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import shallowEqual from 'shallowequal';
import _ from 'underscore';

import { allItemsExistInRange } from '~commonInfra/components/SparseCachedList';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { DatasourceNameCache, REPOSITORY_URL_FIELD, TABLEAUSERVER_CONNECTION_TYPE } from '~commonInfra/utils/DatasourceNameCache';
import { CollectionsErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Filter } from '~commonInfra/utils/Filter';
import { ServerFilterClauses } from '~commonInfra/utils/ServerFilterClauses';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { Set } from '~commonInfra/utils/Set';
import { nonSystemJobFilter } from '~src/backgroundJob/types/IBackgroundJob';
import {
  ClearMixedContentFilters,
  ContentFilterApplied,
  ContentTypeFilterSelected,
  SetContentFilterNoRequestContent,
  ShowContentTypes,
  SpaceContentsMissingSliceRequest,
  SpaceContentsNextPageWithTokenRequest,
  SpaceContentsRequest,
  UpdateFiltersInBothUrlAndState,
} from '~src/bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { SessionSiteSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Broadcasts } from '~src/broadcasts/resources/Broadcasts';
import { Messages } from '~src/codegen/Localize';
import { CollectionItemsResourceQuery } from '~src/Collections/ResourceQuery/CollectionItemsResourceQuery';
import { CollectionsResourceQuery } from '~src/Collections/ResourceQuery/CollectionsResourceQuery';
import { collectionOwnerCache } from '~src/Collections/space/CollectionOwnersCache';
import { ICollectionInfo } from '~src/Collections/space/ICollectionInfo';
import { ConnectedApplications } from '~src/connectedApplications/resources/ConnectedApplicationsResource';
import { DependentDataSourcesResourceQuery } from '~src/datasource/components/contentDefinitions/DependentDataSourcesInDatasourceDefinition';
import { DataConnectionDefinition } from '~src/datasource/dataConnection/components/contentDefinitions/DataConnectionDefinition';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import {
  clearDraftState,
  convertDraftsToWorkbookDraftContents,
  draftsAvailableInState,
  fetchDraftByReferenceIdRequested,
  fetchDraftsRequested,
} from '~src/drafts/actions/DraftsActions';
import { handleDraftsRequestedAction } from '~src/drafts/sagas/DraftsSagas';
import { selectDrafts } from '~src/drafts/selectors/DraftsSelectors';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { DraftsState, WorkbookDraftContent } from '~src/drafts/types/DraftsTypes';
import { getUnpublishedWorkbookCount, shouldProjectsIncludeUnpublishedWorkbookCount } from '~src/drafts/utils/DraftUtils';
import { ExtractTaskDefinition } from '~src/extractTask/components/spaces/contentDefinitions/ExtractTaskDefinition';
import { Favorites } from '~src/favorites/resources/Favorites';
import { GetContentInFavoritesOrderResource } from '~src/favorites/resources/GetContentInFavoritesOrderResource';
import { RunFlowTaskDefinition } from '~src/flow/contentDefinitions/RunFlowTaskDefinition';
import { withFlowAnalyticsExtensionConnection } from '~src/flow/resources/FlowConnectionsQuery';
import { fetchHomeContent } from '~src/home/sagas/HomeApiSaga';
import { ISliceable } from '~src/httpClient/fetchers/ISliceable';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ListResultWithToken } from '~src/httpClient/fetchers/ListResultWithToken';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { IResourceQuery, ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { ResourceQueryWithPagingToken } from '~src/httpClient/resources/ResourceQueryWithPagingToken';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { PublishedConnectionColumnDefinition } from '~src/publishedConnection/contentDefinitions/PublishedConnectionColumnDefinition';
import { IPublishedConnectionDetailInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnectionTableDefinition } from '~src/publishedConnectionTable/contentDefinitions/PublishedConnectionTableDefinition';
import { Recents } from '~src/recents/resources/Recents';
import { getFetchRecommendationsSucceededAction } from '~src/recommendations/actions/FetchRecommendationsActions';
import { ViewRecommendations } from '~src/recommendations/services/ViewRecommendations';
import { GetArchivedContentRequest } from '~src/recycleBin/serverApi/requests/GetArchivedContentRequest';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { ResourceGovernanceService } from '~src/schedule/types/ResourceGovernanceService';
import { scheduleSubspaceNameMatchesSchedule } from '~src/schedule/utils/ScheduleUtils';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SharedWithMe } from '~src/sharing/requests/SharedWithMe';
import { getExplorableItemDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { AssetListItem } from '~src/spacesPlatform/models/AssetListItem';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import {
  DataConnectionContentType,
  FlowOutputStepRunContentType,
  PublishedConnectionColumnContentType,
  PublishedConnectionTableContentType,
  RunFlowTaskContentType,
  SubscriptionContentType,
} from '~src/spacesPlatform/models/ContentTypes';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import {
  FilterFieldNames,
  IFieldFilterClause,
  IFilterClause,
  IStringValuedFilterClause,
  IValuedFieldFilterClause,
} from '~src/spacesPlatform/models/IFilterClause';
import { IRange } from '~src/spacesPlatform/models/IRange';
import { OrderKeys } from '~src/spacesPlatform/models/OrderKeys';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { areValid, getDefaultOrderForContentTypesOrSpaceInfo } from '~src/spacesPlatform/models/sort/SortOptionDefinitions';
import { Content } from '~src/spacesPlatform/resources/Content';
import { Search } from '~src/spacesPlatform/resources/Search';
import {
  selectCurrentlyApplicableContentTypes,
  selectDefaultContentTypeFilterValues,
  selectMixedContentFilterClauses,
  selectSearchText,
  selectSiteSpaceContentType,
} from '~src/spacesPlatform/selectors/ContentFilterSelectors';
import { selectContentFromAppStateWithoutFilters, selectContentNextPageToken, selectMoreItemsForTokenRequest } from '~src/spacesPlatform/selectors/ContentSelectors';
import { selectContentSort } from '~src/spacesPlatform/selectors/ContentSortSelectors';
import { NavLocationSelectors } from '~src/spacesPlatform/selectors/NavigationStateSelectors';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '~src/spacesPlatform/state/SiteSpaceContentTypeState';
import { normalizeSliceInfo, SliceInfo } from '~src/spacesPlatform/utils/MixedContentHelpers';
import { GroupSets } from '~src/userManagement/groupsets/GroupSets';
import { IGroupSetBasicInfo } from '~src/userManagement/groupsets/models/IGroupSetInfo';
import { UserCategory } from '~src/userManagement/users/serverApi/types/UserCategory';

import { updateSpaceInfoCountAction } from '../../actions/ContentCountsActions';
import { clearFilterContent, ShowContentTypesAction } from '../../actions/ContentExplorerFilterActions';
import { deselectAll } from '../../actions/ContentExplorerSelectionActions';
import {
  adjustFiltersForSiteUserSpaceCollectionType,
  containsFilterClause,
  removeFilterClauseFromFilters,
} from '../../actions/filters/FilterHelpers';
import { getFilterClausesForTypes } from '../../actions/filters/getFilterClausesForTypes';
import { updateContentDetail } from '../../actions/GetContentDetailAction';
import {
  spaceContentNextPageWithTokenSucceeded,
  SpaceContentsMissingSliceRequestAction,
  SpaceContentsNextPageWithTokenRequestAction,
  SpaceContentsRequestAction,
  spaceContentsRequestFailed,
  spaceContentsRequestSucceeded,
  spaceContentsSliceSucceeded,
  spaceContentsWithTokenRequestSucceeded,
} from '../../actions/RequestSpaceContentsActions';
import { updateAssetListItems } from '../../actions/UpdateAssetListItemsAction';
import { FilterMap } from '../../state/ContentExplorerState';
import {
  DatasourceSubspaceName,
  datasourceSubspaceNameToExplorableItemType,
  GroupSubspaceName,
  groupSubspaceNameToExplorableItemType,
  isAllGroupSetsSpace,
  isAllGroupsSpace,
  isAllLabelsSpace,
  isAllSchedulesSpace,
  isAllServerUsersSpace,
  isAllSitesSpace,
  isAllUserIdentitiesSubspace,
  isBackgroundJobsSpace,
  isCatalogSpace,
  isCollectionsSpace,
  isConnectedApplicationSpace,
  isDatasourceSpace,
  isFavoritesSpace,
  isFlowSpace,
  isGroupSetGroupsSubspace,
  isGroupSpace,
  isHomeSpace,
  isKeysetPaginationSpace,
  isMixedContentSpace,
  isMyCollectionsSubspace,
  isPersonalSpace,
  isProjectSpace,
  isPublishedConnectionSpace,
  isPublishedConnectionTableSpace,
  isRecentsSpace,
  isRecommendationsSpace,
  isRecycleBinSpace,
  isResourceGovernanceSpace,
  isScheduleSpace,
  isSearchSpace,
  isServerSettingsSpace,
  isServerTasksSpace,
  isSharedWithMeSpace,
  isSingleCollectionSpace,
  isSiteBroadcastsSpace,
  isSiteSettingsSpace,
  isSiteSpace,
  isSiteTasksSpace,
  isSiteUserCollectionsSubspace,
  isSiteUserContentSubspace,
  isSiteUserRecycleBinSubspace,
  isSiteUsersIntegrationSubspace,
  isSiteUserSpace,
  isSiteUsersSpace,
  isSomeKindaSearchSpace,
  isSpaceWithDrafts,
  isUnifiedSearchSpace,
  isWorkbookSpace,
  isWorkbookSpaceWithLuid,
  NullProjectIdForNonProjects,
  PublishedConnectionSubspaceName,
  publishedConnectionSubspaceNameToExplorableItemType,
  ScheduleSubspaceName,
  scheduleSubspaceNameToExplorableItemType,
  SearchSubspaceName,
  searchSubspaceNameToExplorableItemType,
  ServerTasksSubspaceName,
  serverTasksSubspaceNameToExplorableItemType,
  SiteTasksSubspaceName,
  siteTasksSubspaceNameToExplorableItemType,
  SiteUserSubspaceName,
  siteUserSubspaceNameToExplorableItemType,
  SpaceInfo,
  SubspaceName,
  WorkbookSubspaceName,
  workbookSubspaceNameToExplorableItemType
} from '../../state/SpaceInfo';
import {
  applyMixedContentFilter,
  clearMixedContentFilters,
  filterByContentType,
  setContentFilterNoRequestContent,
  updateFiltersInBothUrlAndState,
} from './FilterContentSagas';

export const MixedContentSagas = {
  updateProjectWorkbookCounts,
};

// We've chosen these page sizes via "trial and error" to get a reasonably fast user experience
// when changing sort / filter / content type and scrolling in list and card view. (TFS 841819)
export const PAGESIZE_SLICE: number = 600;
export const PAGESIZE_INITIAL_LOAD: number = 100;

export function* updateQueryParams(contentTypes: ReadonlyArray<string>): SagaIterator {
  const defaultContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = yield select(selectDefaultContentTypeFilterValues);
  const defaultContentTypesSet = Set.fromKeyArray(defaultContentTypeFilterValues);
  const newContentTypesSet = Set.fromKeyArray(contentTypes);

  if (defaultContentTypesSet.containsSameItemsAs(newContentTypesSet)) {
    const search = WindowLocationService.getHashQueryObject();
    if (_.has(search, 'contentType')) {
      WindowLocationService.deleteQueryParameter('contentType');
    }
  } else {
    WindowLocationService.setHashQueryParameter('contentType', contentTypes.join(','), { encode: false });
  }
}

export namespace InProgressRequestsTracker {
  let inProgressRequestPromisesByStartIndex: { [startIndex: number]: Promise<any> } = {};

  export function markRequestInProgress(startIndex: number, requestPromise: Promise<any> = Promise.resolve()): void {
    inProgressRequestPromisesByStartIndex[startIndex] = requestPromise;
  }

  export function getRequestPromiseByStartIndex(startIndex: number): Promise<any> {
    return inProgressRequestPromisesByStartIndex[startIndex];
  }

  export function isRequestInProgress(startIndex: number): boolean {
    return !!inProgressRequestPromisesByStartIndex[startIndex];
  }

  export function markRequestComplete(startIndex: number): void {
    delete inProgressRequestPromisesByStartIndex[startIndex];
  }

  export function clear(): void {
    inProgressRequestPromisesByStartIndex = {};
  }
}

export function clearInProgressRequestsForMissingSlices() {
  InProgressRequestsTracker.clear();
}

export namespace InProgressRequestsTrackerForKeysetPaginationRequest {
  let inProgress: boolean = false;

  export function markRequestInProgress(): void {
    inProgress = true;
  }

  export function isRequestInProgress(): boolean {
    return inProgress;
  }

  export function markRequestComplete(): void {
    inProgress = false;
  }
}

export function fetchContentForInitialPageWithToken<T>(resource: ResourceQueryWithPagingToken<T>): Promise<ListResultWithToken<T>> {
  InProgressRequestsTrackerForKeysetPaginationRequest.markRequestInProgress();
  return resource.getPage(null, PAGESIZE_INITIAL_LOAD).then(result => {
    InProgressRequestsTrackerForKeysetPaginationRequest.markRequestComplete();
    return result;
  });
}

export function fetchContentForNextPageWithToken<T>(resource: ResourceQueryWithPagingToken<T>, nextPageToken: string): Promise<ListResultWithToken<T>> {
  InProgressRequestsTrackerForKeysetPaginationRequest.markRequestInProgress();
  return resource.getPage(nextPageToken, PAGESIZE_SLICE).then(result => {
    InProgressRequestsTrackerForKeysetPaginationRequest.markRequestComplete();
    return result;
  });
}

/**
 * Fetches content for the explorer for the initial slice. Because the page sizes could be different for
 * the initial slice vs. subsequent slices, the initial request is not tracked for de-duping
 * like the missing slice calls are.
 */
export function fetchContentForInitialSlice<T>(resource: ISliceable<T>): Promise<ListResult<T>> {
  clearInProgressRequestsForMissingSlices();
  return resource.getSlice(0, PAGESIZE_INITIAL_LOAD);
}

/**
 * Fetches content for the explorer for missing slices.
 * This function keeps track of in-progress requests, so that it doesn't make duplicate calls.
 * Returns the request promise for the given start index.
 */
export function fetchContentForMissingSlice<T>(resource: ISliceable<T>, startIndex: number, pageSize: number): Promise<ListResult<T>> {
  if (!InProgressRequestsTracker.isRequestInProgress(startIndex)) {
    const requestPromise = resource.getSlice(startIndex, pageSize).then(result => {
      InProgressRequestsTracker.markRequestComplete(startIndex);
      return result;
    });

    InProgressRequestsTracker.markRequestInProgress(startIndex, requestPromise);

    return requestPromise;
  } else {
    return InProgressRequestsTracker.getRequestPromiseByStartIndex(startIndex);
  }
}

export function* getFiltersForContentTypes(projectId: string, contentTypes: ReadonlyArray<ExplorableItemType>): SagaIterator {
  const mixedContentFilter = yield select(selectMixedContentFilterClauses);
  const appStateContentTypes: ReadonlyArray<ExplorableItemType> = yield select(selectCurrentlyApplicableContentTypes);
  const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);

  return getFilterClausesForTypes(
    mixedContentFilter,
    contentTypes,
    appStateContentTypes,
    projectId,
    spaceInfo);
}

export function* getSiteResourceQueryForCatalogSpaceContentType(siteSpaceContentType: SiteSpaceContentType): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    if (!isCatalogSpace(spaceInfo)) {
      return;
    }

    const resource: IResourceQuery<any> = yield call(
      getProjectOrSiteResourceQueryForContentTypes, [convertSiteContentTypeToContentType(siteSpaceContentType)]
    );
    return resource;

  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getSiteResourceQueryForSiteSpaceContentType(siteSpaceContentType: SiteSpaceContentType): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    if (!isSiteSpace(spaceInfo)) {
      return;
    }

    const resource: IResourceQuery<any> = yield call(
      getProjectOrSiteResourceQueryForContentTypes, [convertSiteContentTypeToContentType(siteSpaceContentType)]
    );

    // The project type on the site page means to get all projects not just the top level projects
    if (siteSpaceContentType === 'project') {
      const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
      return resource.withFilters(filters);
    }
    return resource;

  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

/*
 * param orderOverride(optional)- for the pages i.e. home page where storing the order in redux state does not make sense
 */
export function* getRecentsResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypes: IFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    // ToDo: W-15433707 Enable GetDetailedRecents endpoint to support View-core sorts as well as "Modified"
    // we don't want to include the actual filter content type for getDetailedRecents
    const recentFilters = filtersForContentTypes.filter((clause: any) => {
      return !(clause.field && clause.field === 'contentType');
    });

    return Recents.all(contentTypes)
      .withOrder(order)
      .withFilters(recentFilters);

  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getPersonalSpaceResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const currentUserId: string = yield select(SessionUserSelectors.sessionUserId);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    if (contentTypes.length !== 1) {
      return Content.forPersonalSpace(currentUserId, filtersForContentTypes).withOrder(order);
    } else {
      const shownContentType = contentTypes[0];
      let resource = getExplorableItemDefinition(shownContentType).getBasicQuery()
        .withFilters(filtersForContentTypes)
        .addFilter(ServerFilterClauses.serverFilterClauses.owner(currentUserId))
        .addFilter(ServerFilterClauses.serverFilterClauses.usersPersonalSpaceContent('true'))
        .withOrder(order);
      return resource;
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getFavoritesResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    if (contentTypes.length !== 1) {
      return Content.forFavoritesFromCRASS(filtersForContentTypes).withOrder(order);

    } else {
      // this makes requests to all the different content services 
      const shownContentType = contentTypes[0];
      let resource = getExplorableItemDefinition(shownContentType).getBasicQuery()
        .withFilters(filtersForContentTypes)
        .addFilter(ServerFilterClauses.serverFilterClauses.favorite('true'))
        .withOrder(order);

      if (shownContentType === 'project') {
        // we want all projects not just top level ones
        const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
        resource = resource.withFilters(filters);
      }

      // Since the regular APIs don't handle the favorite position or date added sort orders
      // use GetContentInFavoritesOrder to handle that
      if (order.length === 1 && (
        Order.startsWithField(order, Order.asc(OrderKeys.position).field) ||
        Order.startsWithField(order, Order.asc(OrderKeys.dateAdded).field))) {
        return new GetContentInFavoritesOrderResource(resource, shownContentType as ExploreContentApi_ContentType, order[0]);
      }

      return resource;
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getArchivedContentsResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>,
                                                                 orderOverride?: IOrderClause[],
                                                                 ownerId?: string): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    let filtersForContentTypes: IFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);
    if (ownerId) {
      filtersForContentTypes = filtersForContentTypes.concat([ServerFilterClauses.serverFilterClauses.owner(ownerId)]);
    }

    return new ResourceQuery(new WebClientListResourceFetcher(GetArchivedContentRequest, ServerApiMapper.content))
      .withFilters(filtersForContentTypes)
      .withOrder(order)
      .withExtraParams({ contentType: contentTypes[0] });
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getBroadcastsResourceQuery(contentTypes: ReadonlyArray<ExplorableItemType>): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = yield call(getOrderClause, contentTypes, spaceInfo);
    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    return Broadcasts.getAllBroadcasts().withOrder(order).withFilters(filtersForContentTypes);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getSiteUser_ContentSubspace_ResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, NullProjectIdForNonProjects, contentTypes);
    const filtersForContentTypes = removeFilterClauseFromFilters(filtersForContentTypesInitial, FilterFieldNames.topLevelProject);

    if (contentTypes.length !== 1) {
      if (containsFilterClause(filtersForContentTypes, FilterFieldNames.isFavorite)) {
        const filters = removeFilterClauseFromFilters(filtersForContentTypes, FilterFieldNames.isFavorite);
        filters.push(ServerFilterClauses.serverFilterClauses.owner(spaceInfo.item.id));
        return Content.forFavoritesFromCRASS(filters).withOrder(order);
      }

      return Content.forUser(spaceInfo.item.id)
        .withFilters(filtersForContentTypes)
        .withOrder(order);

    } else {
      const shownContentType = contentTypes[0];
      let resource = getExplorableItemDefinition(shownContentType).getBasicQuery();

      const ownerFilter: IFilterClause = shownContentType === 'dataalert'
        ? ServerFilterClauses.serverFilterClauses.user(spaceInfo.item.id)
        : ServerFilterClauses.serverFilterClauses.owner(spaceInfo.item.id);

      return resource.withOrder(order)
        .withFilters(filtersForContentTypes)
        .addFilter(ownerFilter);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getSiteUser_ResourceQueryForSpaceTypes(spaceInfo: SpaceInfo): SagaIterator {
  const contentType = siteUserSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteUserSubspaceName);

  if (contentType) {
    let resource = getExplorableItemDefinition(contentType).getBasicQuery();
    const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, NullProjectIdForNonProjects, [contentType]);
    let filtersForContentTypes = removeFilterClauseFromFilters(filtersForContentTypesInitial, FilterFieldNames.topLevelProject);

    if (contentType === 'collection') {
      filtersForContentTypes = adjustFiltersForSiteUserSpaceCollectionType(filtersForContentTypes);
    }

    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    const ownerFilter: IFilterClause = spaceInfo.subspaceName === 'alerts'
      ? ServerFilterClauses.serverFilterClauses.user(spaceInfo.item.id)
      : ServerFilterClauses.serverFilterClauses.owner(spaceInfo.item.id);
    return resource.withOrder(order)
      .withFilters(filtersForContentTypes)
      .addFilter(ownerFilter);
  }
}

export function* getAllCollectionsResourceQuery(): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const filters: IFilterClause[] = yield call(getFiltersForContentTypes, NullProjectIdForNonProjects, ['collection']);
    const order: IOrderClause[] = yield call(getOrderClause, ['collection'], spaceInfo);
    let resource = new CollectionsResourceQuery()
      .withOrder(order)
      .withFilters(filters);

    if (isMyCollectionsSubspace(spaceInfo)) {
      const currentUserId: string = yield select(SessionUserSelectors.sessionUserId);
      resource = resource.addFilter(ServerFilterClauses.serverFilterClauses.owner(currentUserId));
    }
    return resource;
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getCollectionItemsResourceQuery(contentTypes: ReadonlyArray<ExplorableItemType>): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);

    const order: IOrderClause[] = yield call(getOrderClause, contentTypes, spaceInfo);
    let filters: IFilterClause[] = yield call(getFiltersForContentTypes, NullProjectIdForNonProjects, contentTypes);

    return new CollectionItemsResourceQuery(spaceInfo.item.luid)
      .withOrder(order)
      .withFilters(filters);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getRecommendationsResourceQuery(): SagaIterator {
  try {
    return ViewRecommendations.viewRecommendations();
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getSharedWithMeResourceQuery(
  contentTypes: ReadonlyArray<ExplorableItemType>,
  orderOverride?: IOrderClause[]
): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    // const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));
    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    return SharedWithMe.getSharedWithMe().withFilters(filtersForContentTypes);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getProjectOrSiteResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>): SagaIterator {
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    if (!(isProjectSpace(spaceInfo) || isSiteSpace(spaceInfo) || isCatalogSpace(spaceInfo))) {
      return;
    }

    const order: IOrderClause[] = yield call(getOrderClause, contentTypes, spaceInfo);
    const projectId = spaceInfo.item.id;

    const filtersForContentTypes: IFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    const filtersForFavorites: IFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    if (contentTypes.length !== 1) {
      if (isProjectSpace(spaceInfo) && containsFilterClause(filtersForContentTypes, FilterFieldNames.isFavorite)) {
        const filters = removeFilterClauseFromFilters(filtersForFavorites, FilterFieldNames.isFavorite);
        filters.push(ServerFilterClauses.serverFilterClauses.project(projectId));
        return Content.forFavoritesFromCRASS(filters).withOrder(order);
      }

      return Content.forProject(projectId, filtersForContentTypes).withOrder(order);

    } else {
      const shownContentType = contentTypes[0];
      let resource = getExplorableItemDefinition(shownContentType).getBasicQuery();
      resource = resource.withFilters(filtersForContentTypes);
      return resource.withOrder(order);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getOrderClause(currentContentTypeFilterValues: ExplorableItemType[], spaceInfo: SpaceInfo): SagaIterator {
  const order = yield select(selectContentSort);
  if (!areValid(order, currentContentTypeFilterValues, spaceInfo)) {
    return getDefaultOrderForContentTypesOrSpaceInfo(currentContentTypeFilterValues, spaceInfo);
  }

  return order;
}

export function* getResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  try {
    if (isWorkbookSpace(spaceInfo)) {

      const contentType = workbookSubspaceNameToExplorableItemType(spaceInfo.subspaceName as WorkbookSubspaceName);
      if (contentType) {
        let resource = getExplorableItemDefinition(contentType).getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

        return resource.withOrder(order)
          .withFilters([ServerFilterClauses.serverFilterClauses.workbook(spaceInfo.item.id)]);
      }

    } else if (isProjectSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getProjectOrSiteResourceQueryForContentTypes, contentTypes);

    } else if (isCatalogSpace(spaceInfo)) {
      const siteSpaceContentType = yield select(selectSiteSpaceContentType);
      return yield call(getSiteResourceQueryForCatalogSpaceContentType, siteSpaceContentType);
    } else if (isSiteSpace(spaceInfo)) {
      const siteSpaceContentType = yield select(selectSiteSpaceContentType);
      return yield call(getSiteResourceQueryForSiteSpaceContentType, siteSpaceContentType);
    } else if (isDatasourceSpace(spaceInfo)) {
      return yield call(getDatasourceResourceQuery, spaceInfo);

    } else if (isFavoritesSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getFavoritesResourceQueryForContentTypes, contentTypes);
    } else if (isRecycleBinSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      if (isSiteUserRecycleBinSubspace(spaceInfo)) {
        return yield call(getArchivedContentsResourceQueryForContentTypes, contentTypes, undefined, spaceInfo.item.id);
      }
      return yield call(getArchivedContentsResourceQueryForContentTypes, contentTypes);
    } else if (isRecentsSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getRecentsResourceQueryForContentTypes, contentTypes);

    } else if (isSiteBroadcastsSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getBroadcastsResourceQuery, contentTypes);

    } else if (isRecommendationsSpace(spaceInfo)) {
      return yield call(getRecommendationsResourceQuery);

    } else if (isSomeKindaSearchSpace(spaceInfo)) {
      return yield call(getSearchSpaceResourceQuery, spaceInfo);

    } else if (isSiteUserContentSubspace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getSiteUser_ContentSubspace_ResourceQueryForContentTypes, contentTypes);

    } else if (isSiteUserSpace(spaceInfo)) {
      return yield call(getSiteUser_ResourceQueryForSpaceTypes, spaceInfo);

    } else if (isSiteUsersSpace(spaceInfo)) {
      return yield call(getAllSiteUsersResourceQuery, spaceInfo);

    } else if (isAllServerUsersSpace(spaceInfo)) {
      return yield call(getAllServerUsersResourceQuery, spaceInfo);

    } else if (FeatureFlags.identityMigrationEnabled && isAllUserIdentitiesSubspace(spaceInfo)) {
      return yield call(getAllUserIdentitiesResourceQuery, spaceInfo);

    } else if (isSiteTasksSpace(spaceInfo)) {
      return yield call(getSiteTasksResourceQuery, spaceInfo);

    } else if (isCollectionsSpace(spaceInfo)) {
      return yield call(getAllCollectionsResourceQuery);

    } else if (isSingleCollectionSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getCollectionItemsResourceQuery, contentTypes);

    } else if (isSharedWithMeSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getSharedWithMeResourceQuery, contentTypes);

    } else if (isPersonalSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getPersonalSpaceResourceQueryForContentTypes, contentTypes);

    } else if (isFlowSpace(spaceInfo)) {
      if (spaceInfo.subspaceName === 'runHistory') {
        const resource = getExplorableItemDefinition('flowOutputStepRun').getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, ['flowOutputStepRun'], spaceInfo);
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        const typeOnlyFilters = _.pick(mixedContentFilter, FlowOutputStepRunContentType.filterKeys);
        let typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFilters));
        return resource.withOrder(order)
          .withFilters(typeOnlyFiltersWithDefaultAsArray)
          .addFilter(ServerFilterClauses.serverFilterClauses.flow(spaceInfo.item.id));
      } else if (spaceInfo.subspaceName === 'connections') {
        const resource = DataConnectionDefinition.getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, [DataConnectionDefinition.itemType], spaceInfo);
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        const typeOnlyFilters = _.pick(mixedContentFilter, DataConnectionContentType.filterKeys);
        const typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFilters));
        const query = resource.withOrder(order)
          .withFilters(typeOnlyFiltersWithDefaultAsArray)
          .addFilter(ServerFilterClauses.serverFilterClauses.flow(spaceInfo.item.id));
        return withFlowAnalyticsExtensionConnection(query, spaceInfo.item.luid, typeOnlyFiltersWithDefaultAsArray, order);
      } else if (spaceInfo.subspaceName === 'scheduledTasks') {
        const resource = RunFlowTaskDefinition.getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, ['runFlowTask'], spaceInfo);
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        const typeOnlyFilters = _.pick(mixedContentFilter, RunFlowTaskContentType.filterKeys);
        const typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFilters));
        return resource.withOrder(order)
          .withFilters(typeOnlyFiltersWithDefaultAsArray)
          .addFilter(ServerFilterClauses.serverFilterClauses.flow(spaceInfo.item.id));
      } else if ((yield select(SessionSiteSelectors.isFlowOutputSubscriptionsEnabled)) && spaceInfo.subspaceName === 'subscriptions') {
        const resource = getExplorableItemDefinition('subscription').getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, ['subscriptions'], spaceInfo);
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        const typeOnlyFilters = _.pick(mixedContentFilter, SubscriptionContentType.filterKeys);
        const typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFilters));
        return resource.withOrder(order)
          .withFilters(typeOnlyFiltersWithDefaultAsArray)
          .addFilter(ServerFilterClauses.serverFilterClauses.flow(spaceInfo.item.id));
      }
    } else if (isPublishedConnectionSpace(spaceInfo)) {
      const contentType = publishedConnectionSubspaceNameToExplorableItemType(spaceInfo.subspaceName as PublishedConnectionSubspaceName);
      if (spaceInfo.subspaceName === 'tables') {
        const resource = PublishedConnectionTableDefinition.getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, ['virtualConnectionTable'], spaceInfo);
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        const typeOnlyFilters = _.pick(mixedContentFilter, PublishedConnectionTableContentType.filterKeys);
        const typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFilters));
        // TODO: TFSID-1260213 ntomsic fix this once getPublishedConnectionTables can take an pconn id filter
        const luids = (spaceInfo.item as IPublishedConnectionDetailInfo).tables.map(table => table.luid);
        return resource.withOrder(order)
          .withFilters(typeOnlyFiltersWithDefaultAsArray)
          .addFilter(Filter.stringFieldInArray(FilterFieldNames.luid, luids));
      } else if (spaceInfo.subspaceName === 'scheduledTasks' && contentType) {
        const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, null, [contentType]);
        const resource = ExtractTaskDefinition.getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
        const publishedConnectionIdFilter: IFilterClause
          = ServerFilterClauses.serverFilterClauses.virtualConnection((spaceInfo.item as IPublishedConnectionDetailInfo).id);
        return resource.withOrder(order)
          .withFilters(filtersForContentTypesInitial)
          .addFilter(publishedConnectionIdFilter);
      } else if (spaceInfo.subspaceName === 'runHistory' && contentType) {
        const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, null, [contentType]);
        const resource = ExtractTaskDefinition.getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
        const publishedConnectionIdFilter: IFilterClause
          = ServerFilterClauses.serverFilterClauses.virtualConnection((spaceInfo.item as IPublishedConnectionDetailInfo).id);
        return resource.withOrder(order)
          .withFilters(filtersForContentTypesInitial)
          .addFilter(publishedConnectionIdFilter);
      }
    } else if (isPublishedConnectionTableSpace(spaceInfo)) {
      if (spaceInfo.subspaceName === 'overview') {
        const resource = PublishedConnectionColumnDefinition.getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, ['virtualConnectionColumn'], spaceInfo);
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        const typeOnlyFilters = _.pick(mixedContentFilter, PublishedConnectionColumnContentType.filterKeys);
        let typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFilters));
        return resource.withOrder(order)
          .withFilters(typeOnlyFiltersWithDefaultAsArray)
          .withExtraParams({ id: spaceInfo.item.id });
      }
      return;
    } else if (isBackgroundJobsSpace(spaceInfo)) {
      return yield call(getBackgroundJobsQuery, spaceInfo);
    } else if (isAllSitesSpace(spaceInfo)) {
      return yield call(getAllSitesResourceQuery, spaceInfo);
    } else if (isAllSchedulesSpace(spaceInfo)) {
      return yield call(getAllSchedulesResourceQuery, spaceInfo);
    } else if (isScheduleSpace(spaceInfo)) {
      return yield call(getScheduleResourceQuery, spaceInfo);
    } else if (isServerTasksSpace(spaceInfo)) {
      return yield call(getServerTasksResourceQuery, spaceInfo);
    } else if (isGroupSpace(spaceInfo)) {
      return yield call(getGroupResourceQuery, spaceInfo);
    } else if (isAllGroupsSpace(spaceInfo)) {
      return yield call(getAllGroupsResourceQuery, spaceInfo);
    } else if (isAllGroupSetsSpace(spaceInfo)) {
      return yield call(getAllGroupSetsResourceQuery, spaceInfo);
    } else if (isGroupSetGroupsSubspace(spaceInfo)) {
      return yield call(getGroupSetGroupsResourceQuery, spaceInfo);
    } else if (isSiteSettingsSpace(spaceInfo)) {
      if (spaceInfo.subspaceName === 'connectedApplications') {
        return yield call(getConnectedApplicationsResourceQuery, spaceInfo);
      }
    } else if (isServerSettingsSpace(spaceInfo)) {
      if (isConnectedApplicationSpace(spaceInfo)) {
        return yield call(getConnectedApplicationsResourceQuery, spaceInfo);
      }
      else if (isResourceGovernanceSpace(spaceInfo)) {
        return yield call(getAllResourceGovernanceQuery, spaceInfo);
      }
    } else if (isAllLabelsSpace(spaceInfo)){
      return yield call(getAllLabelsResourceQuery, spaceInfo);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getBackgroundJobsQuery(spaceInfo: SpaceInfo): SagaIterator {
  // We have both boolean and string type filters in the background jobs space
  let filters: IFieldFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, ['backgroundJob']);

  const isSystemJobsFilterClause = (f: IFieldFilterClause) => {
    return f.field === FilterFieldNames.showSystemJobs;
  };
  const showSystemJobsFilter: IFieldFilterClause | undefined = filters.find(isSystemJobsFilterClause);
  const includeSystemJobs = showSystemJobsFilter
    && (showSystemJobsFilter as IValuedFieldFilterClause<boolean>).value === true;

  // Regardless of truthiness, this filter is a pseudo-filter that shouldn't be in the query
  filters = filters.filter(f => !isSystemJobsFilterClause(f));

  if (includeSystemJobs) {
    // Remove the default filter that filters out any system type job
    filters = filters.filter(f => !shallowEqual(f, nonSystemJobFilter));
  }

  const order: IOrderClause[] = yield call(getOrderClause, ['backgroundJobs'], spaceInfo);
  const resource = getExplorableItemDefinition('backgroundJob')
    .getBasicQuery()
    .withOrder(order)
    .withFilters(filters);
  return resource;
}

export function* getDatasourceResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType = datasourceSubspaceNameToExplorableItemType(spaceInfo.subspaceName as DatasourceSubspaceName);
  if (contentType === 'dependentDataSource') {
    const resource = new DependentDataSourcesResourceQuery(spaceInfo.item.id);
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    return resource.withOrder(order);
  }
  if (contentType) {
    let resource = getExplorableItemDefinition(contentType).getBasicQuery();
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    return resource.withOrder(order)
      .withFilters([ServerFilterClauses.serverFilterClauses.datasource(spaceInfo.item.id)]);
  }
}

export function* buildCrassSearch(): SagaIterator {
  let search = new Search();

  const searchText = yield select(selectSearchText);
  if (searchText || FeatureFlags.searchQuickLinks) {
    search = search.search(searchText);
  }

  const orderClauses: IOrderClause[] = yield select(selectContentSort);
  if (orderClauses && orderClauses.length > 0) {
    search = search.withOrder(orderClauses);
  }

  return search;
}

export function* getSearchSpaceResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  if (FeatureFlags.crassSearch) {
    return yield call(buildCrassSearch);
  }

  const contentType = searchSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SearchSubspaceName);
  if (contentType) {
    let resource = getExplorableItemDefinition(contentType).getBasicQuery();
    const filtersForContentTypes: IFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, [contentType]);
    resource = resource.withFilters(filtersForContentTypes);
    if (contentType === 'project') {
      // we want to search across all projects not just top level ones
      const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
      resource = resource.withFilters(filters);
    }
    const order: IOrderClause[] = yield call(getOrderClause, [], spaceInfo);
    return resource.withOrder(order);
  }
}

export function* getAllSiteUsersResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'siteuser';

  const explorableItemDefintion = getExplorableItemDefinition(contentType);
  let resource = FeatureFlags.UserGridInfiniteScrollEnabled ? explorableItemDefintion.getResourceTokenQuery() :
    explorableItemDefintion.getBasicQuery();

  const filters: IFilterClause[] = yield call(getFiltersForContentTypes, NullProjectIdForNonProjects, [contentType]);
  if (FeatureFlags.uiamIntegrationUserLicenseEnabled && getSessionManagementServiceInstance().isOnlineEnvironment()) {
    const category = isSiteUsersIntegrationSubspace(spaceInfo) ? UserCategory.Integration : UserCategory.Regular;
    filters.push(ServerFilterClauses.UserCategory(category));
  }
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getAllServerUsersResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'serveruser';
  let resource = FeatureFlags.serverUserSolrMigrationEnabled ? getExplorableItemDefinition(contentType).getResourceTokenQuery() :
    getExplorableItemDefinition(contentType).getBasicQuery();

  const filters = yield call(getFiltersForContentTypes, NullProjectIdForNonProjects, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getConnectedApplicationsResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'connectedApplication';
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

  // Locally filters so we don't follow the same ordering schema
  return ConnectedApplications.all(order);
}

export function* getAllUserIdentitiesResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'userIdentityConflict';
  let resource = getExplorableItemDefinition(contentType).getBasicQuery();

  const filters = yield call(getFiltersForContentTypes, NullProjectIdForNonProjects, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getSiteTasksResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType = siteTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteTasksSubspaceName);
  if (contentType) {
    const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, null, [contentType]);
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

    const siteId: string = yield select(SessionSiteSelectors.siteId);
    const siteIdFilter: IFilterClause = ServerFilterClauses.serverFilterClauses.siteId(siteId);

    let resource = getExplorableItemDefinition(contentType)
      .getBasicQuery()
      .withOrder(order)
      .withFilters(filtersForContentTypesInitial)
      .addFilter(siteIdFilter);

    if (areLinkedTasksEnabled() && spaceInfo.subspaceName === 'runFlowTasks') {
      resource = resource.addFilter(ServerFilterClauses.serverFilterClauses.taskType('FlowTask'));
    } else if (areLinkedTasksEnabled() && spaceInfo.subspaceName === 'linkedTasks') {
      resource = resource.addFilter(ServerFilterClauses.serverFilterClauses.taskType('LinkedTask'));
    }
    return resource;
  }
}

export function* getServerTasksResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType = serverTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ServerTasksSubspaceName);
  if (contentType) {
    const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, null, [contentType]);
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

    let resource = getExplorableItemDefinition(contentType)
      .getBasicQuery()
      .withOrder(order)
      .withFilters(filtersForContentTypesInitial);

    if (areLinkedTasksEnabled() && spaceInfo.subspaceName === 'runFlowTasks') {
      resource = resource.addFilter(ServerFilterClauses.serverFilterClauses.taskType('FlowTask'));
    } else if (areLinkedTasksEnabled() && spaceInfo.subspaceName === 'linkedTasks') {
      resource = resource.addFilter(ServerFilterClauses.serverFilterClauses.taskType('LinkedTask'));
    }
    return resource;
  }
}

export function* getAllSitesResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'site';
  const resource = getExplorableItemDefinition(contentType).getBasicQuery();
  const filtersForContentTypes: IFilterClause[] = yield call(getFiltersForContentTypes, NullProjectIdForNonProjects, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filtersForContentTypes);
}

export function* getAllSchedulesResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'schedule';

  let itemTypeDefinition = getExplorableItemDefinition(contentType);
  let resource = itemTypeDefinition.getBasicQuery();

  const filters = _.compact(_.values(itemTypeDefinition.defaultFilters('')));
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getAllGroupsResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'group';
  let resource = getExplorableItemDefinition(contentType).getBasicQuery();

  const filters = yield call(getFiltersForContentTypes, null, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getAllGroupSetsResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'groupset';
  const filters = yield call(getFiltersForContentTypes, null, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return GroupSets.all()
    .withOrder(order)
    .withFilters(filters);
}

export function* getGroupSetGroupsResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'group';
  let resource = GroupSets.getGroupSetGroups(spaceInfo.item as IGroupSetBasicInfo);
  const filters: IFilterClause[] = yield call(getFiltersForContentTypes, null, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  const groupSetGroupsFilter = GroupSets.getGroupSetGroupsFilter(spaceInfo.item as IGroupSetBasicInfo);
  filters.push(groupSetGroupsFilter);
  let result = resource
    .withOrder(order)
    .withFilters(filters);
  return result;
}

export function* getAllResourceGovernanceQuery(spaceInfo: SpaceInfo): SagaIterator {
  return ResourceGovernanceService.all();
}

export function* getAllLabelsResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType: ExplorableItemType = 'labelValue';
  let itemTypeDefinition = getExplorableItemDefinition(contentType);
  let resource = itemTypeDefinition.getBasicQuery();

  const filters = _.compact(_.values(itemTypeDefinition.defaultFilters('')));
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getScheduleResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const explorableItemType = scheduleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ScheduleSubspaceName);
  if (explorableItemType) {
    const scheduledAction = (spaceInfo.item as IScheduleInfo).scheduledAction;
    if (!scheduleSubspaceNameMatchesSchedule(spaceInfo.subspaceName as ScheduleSubspaceName, scheduledAction)) {
      return;
    }

    let itemTypeDefinition = getExplorableItemDefinition(explorableItemType);
    let resource = itemTypeDefinition.getBasicQuery();

    const filters = _.compact(_.values(itemTypeDefinition.defaultFilters('')));
    const inServerArea = yield select(NavLocationSelectors.inServerArea);
    if (!inServerArea) {
      const siteId: string = yield select(SessionSiteSelectors.siteId);
      const siteIdFilter: IFilterClause = ServerFilterClauses.serverFilterClauses.siteId(siteId);
      filters.push(siteIdFilter);
    }

    const scheduleIdFilter = ServerFilterClauses.serverFilterClauses.schedule(spaceInfo.item.id);
    filters.push(scheduleIdFilter);

    const order: IOrderClause[] = yield call(getOrderClause, [explorableItemType], spaceInfo);
    return resource
      .withOrder(order)
      .withFilters(filters);
  }
}

export function* getGroupResourceQuery(spaceInfo: SpaceInfo): SagaIterator {
  const contentType = groupSubspaceNameToExplorableItemType(spaceInfo.subspaceName as GroupSubspaceName);
  if (contentType) {
    let resource = getExplorableItemDefinition(contentType).getBasicQuery();
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    const filters: IFilterClause[] = yield call(getFiltersForContentTypes, null, [contentType]);
    filters.push(ServerFilterClauses.serverFilterClauses.inGroup(spaceInfo.item.id));
    return resource.withOrder(order)
      .withFilters(filters);
  }
}

export function convertToAssetListItem(content: ListResult<IContentInfo>): AssetListItem[] {
  return content.items.map((item, idx) => {
    const assetListItem: AssetListItem = {
      id: item.id,
      objectType: item.contentType,
      objectId: item.id,
      objectName: item.name,
      position: `${idx + 1}`,
      luid: item.luid
    };
    return assetListItem;
  });
}

// exported for testing
export interface ContentAndWorkbookDrafts {
  content: ListResult<IContentInfo>;
  workbookDrafts: WorkbookDraftContent[];
}

// exported for testing
export function updateProjectWorkbookCounts(
  contentTypes: ReadonlyArray<ExplorableItemType>,
  contentAndWorkbookDrafts: ContentAndWorkbookDrafts
): ContentAndWorkbookDrafts {
  if (shouldProjectsIncludeUnpublishedWorkbookCount(contentTypes)) {
    contentAndWorkbookDrafts.workbookDrafts
      .filter(draft => draft.draftStatus === ContentDraftStatus.unpublished)
      .forEach(draft => {
        for (let index = 0; index < contentAndWorkbookDrafts.content.items.length; index++) {
          const item = contentAndWorkbookDrafts.content.items[index];
          if (item.contentType === 'project' && item.luid === draft.containerLuid) {
            (contentAndWorkbookDrafts.content.items[index] as any).workbookCount += 1;
          }
        }
      });

    contentAndWorkbookDrafts.workbookDrafts = [];
  }

  return contentAndWorkbookDrafts;
}

/**
 * W-22576228: After datasource content is fetched, check if any tableauserver connections
 * have unresolved names (repoUrl displayed instead of DS name). If so, do a targeted fetch
 * by repositoryUrl to populate the cache, then re-dispatch affected items to trigger re-render.
 */
export function* resolvePendingDatasourceNames(content: ListResult<IContentInfo>): SagaIterator {
  // Collect unresolved repoUrls: from the pending set AND from content items whose
  // tableauserver connectionDetails.name hasn't been resolved yet (covers the case
  // where search filters exclude the parent DS from the initial response).
  const pendingRepoUrls = DatasourceNameCache.consumePending();

  const unresolvedFromContent: string[] = [];
  content.items.forEach((item: IContentInfo) => {
    const ds = item as IDataSourceInfo;
    if (ds?.connectionDetails?.type !== TABLEAUSERVER_CONNECTION_TYPE) return;
    const name = (ds.connectionDetails as { name?: string }).name;
    if (!name || DatasourceNameCache.resolve(name)) return;
    unresolvedFromContent.push(name);
  });

  const seen: Record<string, boolean> = {};
  const allUnresolved: string[] = pendingRepoUrls.concat(unresolvedFromContent).filter(function(url) {
    if (seen[url]) return false;
    seen[url] = true;
    return true;
  });
  if (allUnresolved.length === 0) return;

  try {
    // Targeted fetch — populates cache via ServerApiMapper.dataSources()
    yield call(() =>
      DataSources.all()
        .withFilters([Filter.stringFieldInArray(REPOSITORY_URL_FIELD, allUnresolved)])
        .getSlice(0, allUnresolved.length)
    );

    // Re-dispatch affected items so cells re-render with resolved names
    const affectedItems = content.items.filter((item: IContentInfo) => {
      const ds = item as IDataSourceInfo;
      return ds?.connectionDetails?.type === TABLEAUSERVER_CONNECTION_TYPE &&
        (ds.connectionDetails as { name?: string }).name &&
        DatasourceNameCache.resolve((ds.connectionDetails as { name?: string }).name);
    });

    if (affectedItems.length > 0) {
      yield put(updateContentDetail(...affectedItems));
    }
  } catch {
    // Non-critical — connections will show repoUrl as fallback
  }
}

export function* processRequestSpaceContent(action: SpaceContentsRequestAction): SagaIterator {
  const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);

  try {
    if (isHomeSpace(spaceInfo)) {
      // Clear search filters before fetching content
      yield put(clearFilterContent());
      yield call(fetchHomeContent);

    } else {
      // A blank search term ('') is ok and will match to every item if quick links are active
      // otherwise, if quick links are disabled, then clear any items and don't get a resource query
      if (isSomeKindaSearchSpace(spaceInfo)) {
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        if (_.isEmpty(mixedContentFilter) || !mixedContentFilter.search ||
          ((mixedContentFilter.search as IStringValuedFilterClause).value.length === 0 && !FeatureFlags.searchQuickLinks)) {
          const noContent: ListResult<IContentInfo> = ListResult.empty<IContentInfo>();
          yield put(spaceContentsRequestSucceeded(noContent, []));
          return;
        }
      }

      const resource = yield call(getResourceQuery, spaceInfo);
      yield put(deselectAll());

      // for cases in which there is no content to fetch,
      // resource can be undefined
      if (!_.isUndefined(resource)) {

        if (FeatureFlags.webAuthoringAutoSaveEnabled) {
          yield put(clearDraftState());
        }

        if (isKeysetPaginationSpace(spaceInfo)) {
          let contentWithToken: ListResultWithToken<IContentInfo> =
            yield call(fetchContentForInitialPageWithToken, resource);
          if (contentWithToken) {
            yield put(spaceContentsWithTokenRequestSucceeded(contentWithToken));
          }

        } else {
          let content: ListResult<IContentInfo>;
          let workbookDrafts: WorkbookDraftContent[];

          const unpublishedWorkbooksSupported = FeatureFlags.webAuthoringAutoSaveEnabled;
          if (unpublishedWorkbooksSupported) {
            const contentTypes: ReadonlyArray<ExplorableItemType> = yield select(selectCurrentlyApplicableContentTypes);
            const initialContentAndWorkbookDrafts = yield all(getInitialContentAndWorkbookDraftsRequests(spaceInfo, resource));
            const updatedContentAndWorkbookDrafts = MixedContentSagas.updateProjectWorkbookCounts(contentTypes, initialContentAndWorkbookDrafts);
            content = updatedContentAndWorkbookDrafts.content;
            workbookDrafts = updatedContentAndWorkbookDrafts.workbookDrafts;
          } else {
            // If we are in the crass search results prototype, a different type of resource is used.
            if (isUnifiedSearchSpace(spaceInfo)) {
              content = yield call(fetchMissingSliceFromCrass, resource, 0, PAGESIZE_INITIAL_LOAD);
            } else {
              content = yield call(fetchContentForInitialSlice, resource);
            }
          }

          if (content) {
            handleGetContentErrors(spaceInfo, content.errors);
            yield put(spaceContentsRequestSucceeded(content, workbookDrafts));

            // update individual count for mixed content space by setting the specific content type as a subspacename
            if (isMixedContentSpace(spaceInfo)) {
              const contentTypes: ExplorableItemType[] = yield select(selectCurrentlyApplicableContentTypes);
              if (contentTypes.length === 1) {
                const thisSpaceInfo: SpaceInfo = { ...spaceInfo, subspaceName: contentTypes[0] as SubspaceName };
                const unpublishedWorkbookCount = contentTypes[0] === 'workbook' ? getUnpublishedWorkbookCount(workbookDrafts) : 0;
                yield put(updateSpaceInfoCountAction(thisSpaceInfo, content.total + unpublishedWorkbookCount));
              }
            } else {
              yield put(updateSpaceInfoCountAction(spaceInfo, content.total));
            }

            updateOwnerCache(spaceInfo, content);

            if (isFavoritesSpace(spaceInfo)) {
              let contentTypes = yield select(selectCurrentlyApplicableContentTypes);
              const assetListItems: ListResult<AssetListItem> = yield call(getFavoritesForContentTypes, contentTypes);
              if (assetListItems) {
                yield put(updateAssetListItems(assetListItems.items));
              }
            }

            // convert Collection object into AssetListItem, this data is used for reordering collections.
            if (isSingleCollectionSpace(spaceInfo)) {
              const assetListItems: AssetListItem[] = convertToAssetListItem(content);
              yield put(updateAssetListItems(assetListItems));
            }

            if (isRecommendationsSpace(spaceInfo)) {
              yield put(getFetchRecommendationsSucceededAction(content));
            }

            if (!unpublishedWorkbooksSupported) {
              yield call(fetchDraftsIfNeeded, spaceInfo);
            }

            // W-22576228: Resolve pending datasource names for composed DS connections.
            // resolvePendingDatasourceNames is a no-op if nothing is pending.
            yield call(resolvePendingDatasourceNames, content);
          }
        }
      }
    }
  } catch (e) {
    yield put(spaceContentsRequestFailed());
    const errorMessage = getErrorMessage(spaceInfo, e);
    yield put(requestFailed(e, errorMessage));
  }
}

export function getInitialContentAndWorkbookDraftsRequests(
  spaceInfo: SpaceInfo,
  resource: any
): Partial<Record<'content' | 'workbookDrafts', CallEffect>> {
  return {
    content: isUnifiedSearchSpace(spaceInfo) ? call(fetchMissingSliceFromCrass, resource, 0, PAGESIZE_INITIAL_LOAD) :
      call(fetchContentForInitialSlice, resource),
    workbookDrafts: call(fetchDraftsIfNeeded, spaceInfo),
  };
}

export function handleGetContentErrors(spaceInfo: SpaceInfo, errors: IError[]) {
  const toasterService = ToasterService.getInstance();
  errors && errors.forEach(error => {
    if (error.code === CollectionsErrorCode.UNABLE_TO_FETCH_FROM_CONTENT_SERVICE) {
      if (isCollectionsSpace(spaceInfo) || isSiteUserCollectionsSubspace(spaceInfo)) {
        toasterService.pop({
          type: ToastType.Warning,
          text: Messages.unableToFetchAllCollectionInfo(),
        });
      } else if (isSingleCollectionSpace(spaceInfo)) {
        toasterService.pop({
          type: ToastType.Warning,
          text: Messages.unableToFetchAllCollectionItems(),
        });
      }
    }
  });
}

function getErrorMessage(spaceInfo: SpaceInfo, e: Error): string {
  if (isSearchSpace(spaceInfo)) {
    switch (spaceInfo.subspaceName) {
      case 'tables':
      case 'tablesWithCrass':
        return Messages.catalogingTablesSearchErrorMessage();
      case 'databases':
      case 'databasesWithCrass':
        return Messages.catalogingDatabasesSearchErrorMessage();
    }
  } else if (isSingleCollectionSpace(spaceInfo)) {
    const errorCode = (e as any).data.errorCode;
    if (errorCode === CollectionsErrorCode.UNABLE_TO_FETCH_FROM_CONTENT_SERVICE) {
      return Messages.unableToFetchAllCollectionItems();
    }
  }

  return Messages.errorUnknown();
}

function getFavoritesForContentTypes(contentTypes: ExploreContentApi_ContentType[]): Promise<ListResult<AssetListItem>> {
  // TODO: All favorites code depends on fetch size of 1000 items. Should use consistent named constant for this.
  return Favorites.fetchFromExploreFavoriteContentForUser(contentTypes).getSlice(0, 1000);
}

/**
 * Next page is valid only for the current explorer query, which is set up via various actions
 * (those that set the order, filters, etc. in the store) but initiated by a SpaceContentsRequest
 * (that is where the current space contents get cleared out and re-populated with the contents of
 * the new query.
 *
 * Hence, if we ever get a SpaceContentsRequest, all pending missing next page requests must be cancelled,
 * otherwise next page from the old query would be inserted into the new query's results.
 */
export function* processRequestSpaceContentNextPageTokenWithCancellation(action: SpaceContentsNextPageWithTokenRequestAction): SagaIterator {
  const nextPageTask = yield fork(processRequestSpaceContentNextPageWithToken, action);

  yield take(SpaceContentsRequest);
  yield cancel(nextPageTask);
}

export function* processRequestSpaceContentNextPageWithToken(action: SpaceContentsNextPageWithTokenRequestAction): SagaIterator {
  try {
    if (!InProgressRequestsTrackerForKeysetPaginationRequest.isRequestInProgress()) {
      const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
      const resource = yield call(getResourceQuery, spaceInfo);
      let contentWithToken: ListResultWithToken<IContentInfo> =
        yield call(fetchContentForNextPageWithToken, resource, action.nextPageToken);
      if (contentWithToken) {
        yield put(spaceContentNextPageWithTokenSucceeded(contentWithToken));
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

/**
 * Missing slices are valid only for the current explorer query, which is set up via various actions
 * (those that set the order, filters, etc. in the store) but initiated by a SpaceContentsRequest
 * (that is where the current space contents get cleared out and re-populated with the contents of
 * the new query.
 *
 * Hence, if we ever get a SpaceContentsRequest, all pending missing slice requests must be cancelled,
 * otherwise slices from the old query would be inserted into the new query's results.
 */
export function* processRequestSpaceContentMissingSliceWithCancellation(pageSize: number, action: SpaceContentsMissingSliceRequestAction): SagaIterator {
  const missingSliceTask = yield fork(processRequestSpaceContentMissingSliceInner, pageSize, action);

  yield take(SpaceContentsRequest);
  yield cancel(missingSliceTask);
}

export function* processRequestSpaceContentMissingSliceInner(pageSize: number, action: SpaceContentsMissingSliceRequestAction): SagaIterator {
  const itemRange: IRange = { start: action.start, end: action.end };

  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const resource = yield call(getResourceQuery, spaceInfo);

    if (isKeysetPaginationSpace(spaceInfo)) {
      let moreItems: boolean = yield select(selectMoreItemsForTokenRequest);
      let nextPageToken: string = yield select(selectContentNextPageToken);
      while (moreItems) {
        let contentWithToken: ListResultWithToken<IContentInfo> =
          yield call(fetchContentForNextPageWithToken, resource, nextPageToken);
        if (contentWithToken) {
          yield put(spaceContentNextPageWithTokenSucceeded(contentWithToken));
        }
        moreItems = yield select(selectMoreItemsForTokenRequest);
        nextPageToken = yield select(selectContentNextPageToken);
      }
    } else {
      // Grab items straight for redux, this can't be filtered (ala selectContentFromAppState)
      // otherwise it will filter out items we need to fetch
      let items: ReadonlyArray<IContentInfo> = yield select(selectContentFromAppStateWithoutFilters);

      const unpublishedWorkbooksSupported = FeatureFlags.webAuthoringAutoSaveEnabled;
      if (unpublishedWorkbooksSupported) {
        const normalizedSliceInfo: SliceInfo = yield call(normalizeSliceInfo, items, action.start, action.end);
        items = normalizedSliceInfo.items.slice();
        itemRange.start = normalizedSliceInfo.range.start;
        itemRange.end = normalizedSliceInfo.range.end;
      }

      const pageIndexOfFirstItem = Math.floor(itemRange.start / pageSize);
      const pageIndexOfLastItem = Math.floor(itemRange.end / pageSize);

      for (let pageIndex = pageIndexOfFirstItem; pageIndex <= pageIndexOfLastItem; pageIndex++) {

        const startIndexToFetch = pageSize * pageIndex;
        const endIndexToFetch = startIndexToFetch + pageSize - 1;

        if (!allItemsExistInRange(items, startIndexToFetch, endIndexToFetch)) {
          // If we are in the crass search results prototype, a different type of resource is used.
          let content: ListResult<IContentInfo>;
          let workbookDrafts: WorkbookDraftContent[];

          if (unpublishedWorkbooksSupported && spaceInfo.spaceType !== 'group') {
            const contentTypes: ReadonlyArray<ExplorableItemType> = yield select(selectCurrentlyApplicableContentTypes);
            const slicedContentAndWorkbookDrafts = yield all(getSlicedContentAndWorkbookDraftsRequests(spaceInfo, resource, startIndexToFetch, pageSize));
            const updatedContentAndWorkbookDrafts = MixedContentSagas.updateProjectWorkbookCounts(contentTypes, slicedContentAndWorkbookDrafts);
            content = updatedContentAndWorkbookDrafts.content;
            workbookDrafts = updatedContentAndWorkbookDrafts.workbookDrafts;
          } else {
            if (isUnifiedSearchSpace(spaceInfo)) {
              content = yield call(fetchMissingSliceFromCrass, resource, startIndexToFetch, pageSize);
            } else {
              content = yield call(fetchContentForMissingSlice, resource, startIndexToFetch, pageSize);
            }
          }

          if (content) {
            yield put(spaceContentsSliceSucceeded(startIndexToFetch, content, workbookDrafts, action.appendOnly));

            if (!unpublishedWorkbooksSupported) {
              yield call(fetchDraftsIfNeeded, spaceInfo);
            }

            updateOwnerCache(spaceInfo, content);
          }
        }
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getSlicedContentAndWorkbookDraftsRequests(
  spaceInfo: SpaceInfo,
  resource: any,
  startIndex: number,
  pageSize: number
): Partial<Record<'content' | 'workbookDrafts', CallEffect>> {
  return {
    content: isUnifiedSearchSpace(spaceInfo) ? call(fetchMissingSliceFromCrass, resource, startIndex, pageSize) :
      call(fetchContentForMissingSlice, resource, startIndex, pageSize),
    workbookDrafts: call(fetchDraftsIfNeeded, spaceInfo),
  };
}

// CRASS Prototype Code
export function fetchMissingSliceFromCrass(resource: Search, startIndex: number, pageSize: number): Promise<ListResult<IContentInfo>> {
  const pageIndex = Math.floor(startIndex / pageSize);
  return resource.page(pageIndex).limit(pageSize).getResults().then(searchResult => {
    const content: ListResult<IContentInfo> = {
      index: startIndex,
      total: searchResult.total,
      items: [...searchResult.items]
    };
    return content;
  });
}

// exported for testing
export function updateOwnerCache(spaceInfo: SpaceInfo, content: ListResult<IContentInfo<ExplorableItemType>>) {
  if (isSingleCollectionSpace(spaceInfo) && !((spaceInfo.item as ICollectionInfo).owner?.displayName)) {
    // If GET /collections/<luid> doesn't return the full owner info, fetch it-- this can also happen with user visibility limited but that seems ok
    collectionOwnerCache.updateCollectionOwners([spaceInfo.item as ICollectionInfo], 'collection');

  }
}

export function* processShowContentTypes(action: ShowContentTypesAction): SagaIterator {
  try {
    yield call(updateQueryParams, action.contentTypes);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* fetchDraftsIfNeeded(spaceInfo: SpaceInfo): SagaIterator {
  if (!FeatureFlags.webAuthoringAutoSaveEnabled) {
    // In we navigate to a site with the FF disabled from a site that previously had it enabled, workbooks will
    // incorrectly display the draft banner due to stale application state so we need to clear the draft state
    // in this case. Note this is only needed when switching between sites with FF values going from ON -> OFF and
    // does not have any side effects when going from ON -> ON, OFF -> ON, or OFF -> OFF.
    yield put(clearDraftState());
    return [];
  } else if (isWorkbookSpaceWithLuid(spaceInfo)) {
    yield put(fetchDraftByReferenceIdRequested(spaceInfo.spaceType, spaceInfo.item.luid));
    return [];
  } else if (isSpaceWithDrafts(spaceInfo)) {
    // There's no need to fetch drafts if they already exist in the store.
    const draftsState: DraftsState = yield select(selectDrafts);
    const contentFilters: FilterMap = yield select(selectMixedContentFilterClauses);
    const contentTypes: ReadonlyArray<ExplorableItemType> = yield select(selectCurrentlyApplicableContentTypes);
    if (FeatureFlags.webAuthoringAutoSaveEnabled) {
      if (draftsState?.drafts) {
        yield put(draftsAvailableInState(draftsState.drafts));
        return convertDraftsToWorkbookDraftContents(Object.values(draftsState.drafts));
      } else {
        const action = fetchDraftsRequested(spaceInfo, contentFilters, contentTypes);
        return yield call(handleDraftsRequestedAction, action);
      }
    } else if (draftsState?.drafts) {
      yield put(draftsAvailableInState(draftsState.drafts));
    } else {
      yield put(fetchDraftsRequested(spaceInfo, contentFilters, contentTypes));
    }
    return [];
  }

  const contentTypes: ReadonlyArray<ExplorableItemType> = yield select(selectCurrentlyApplicableContentTypes);
  if (shouldProjectsIncludeUnpublishedWorkbookCount(contentTypes)) {
    const contentFilters: FilterMap = yield select(selectMixedContentFilterClauses);
    const shouldDispatchDraftActions = false;
    const action = fetchDraftsRequested(spaceInfo, contentFilters, contentTypes, shouldDispatchDraftActions);
    return yield call(handleDraftsRequestedAction, action);
  }
  return [];
}

export function* rootMixedContentSaga(): SagaIterator {
  yield takeLatest(SpaceContentsRequest, processRequestSpaceContent);
  yield takeLatest(ContentTypeFilterSelected, filterByContentType);
  yield takeLatest(ShowContentTypes, processShowContentTypes);
  yield takeEvery(SpaceContentsMissingSliceRequest, processRequestSpaceContentMissingSliceWithCancellation, PAGESIZE_SLICE);
  yield takeEvery(SpaceContentsNextPageWithTokenRequest, processRequestSpaceContentNextPageTokenWithCancellation);
  yield takeLatest(ContentFilterApplied, applyMixedContentFilter);
  yield takeLatest(SetContentFilterNoRequestContent, setContentFilterNoRequestContent);
  yield takeLatest(ClearMixedContentFilters, clearMixedContentFilters);
  yield takeEvery(UpdateFiltersInBothUrlAndState, updateFiltersInBothUrlAndState);
}
